import { useMutation } from '@apollo/client';
import { FormProvider } from 'react-hook-form';
import { useNavigate } from 'react-router';
import { toast } from 'react-toastify';
import invariant from 'tiny-invariant';
import { z } from 'zod';
import { graphql } from '~/apollo/generated/v4';
import { Button } from '~/components/ui/button';
import { HookFormErrors } from '~/components/ui/forms/HookFormErrors';
import { useZodForm } from '~/utils/forms';
import type { VftPartsFragment } from '~/apollo/generated/v4/graphql';
import { VftFormFields } from './vft-form-fields';
import { uploadVftIdRoute } from '~/paths';
import { initialVft, vftSchema } from '~/utils/modules/vft';

const CREATE_VFT = graphql(`
  mutation VirtualFieldTripCreate($input: VirtualFieldTripCreateInput!) {
    virtualFieldTripCreate(input: $input) {
      result {
        id
        lifecycleStatus
        name
        outcropTour
      }
    }
  }
`);

export const ADD_OUTCROP_TO_VFT = graphql(`
  mutation AddOutcropToVft($input: VirtualFieldTripAddOutcropInput!) {
    virtualFieldTripAddOutcrop(input: $input)
  }
`);

export function CreateVftForm() {
  const navigate = useNavigate();
  const [createVft, { loading, error }] = useMutation(CREATE_VFT);
  const [addOutcrop, { loading: addOutcropLoading, error: addOutcropError }] =
    useMutation(ADD_OUTCROP_TO_VFT);

  const form = useZodForm({
    schema: vftSchema,
    values: initialVft(),
  });

  const handleSubmit = form.handleSubmit(
    async (values: z.infer<typeof vftSchema>) => {
      const { ...input } = values;
      const outcrops = input.outcrops || [];
      delete input.outcrops;
      try {
        const res = await createVft({
          variables: { input },
        });
        const vftId = res.data?.virtualFieldTripCreate?.result?.id;
        if (!vftId) {
          toast.error('There was a problem saving the vft.');
          return;
        }
        for (const outcropId of outcrops) {
          try {
            await addOutcrop({
              variables: {
                input: {
                  virtualFieldTripId: Number(vftId),
                  outcropId: outcropId,
                },
              },
            });
          } catch (err) {
            console.log('Error saving vft outcrop', err);
            toast.error(`There was a problem saving outcrop ${outcropId} to vft.`);
          }
        }
        const redirectPath = uploadVftIdRoute(parseInt(vftId));
        navigate(redirectPath);
        toast.success('VFT saved successfully.');
      } catch (err) {
        console.log('Error updating vft', err);
        toast.error('There was a problem saving the vft.');
      }
    },
  );

  return (
    <FormProvider {...form}>
      <form onSubmit={handleSubmit} className="space-y-4">
        <VftFormFields />
        <HookFormErrors graphQLError={error || addOutcropError} />

        <div className="text-center">
          <Button type="submit" color="primary" disabled={loading || addOutcropLoading}>
            Save
          </Button>
        </div>
      </form>
    </FormProvider>
  );
}
